import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	PromptDialog,
	type PromptDialogProps,
} from '../components/dialogs/PromptDialog';

/** One text question for `usePromptDialog`. Pass translated strings. */
export type PromptRequest = Pick<PromptDialogProps, 'title' | 'label'> &
	Partial<
		Pick<
			PromptDialogProps,
			| 'message'
			| 'defaultValue'
			| 'placeholder'
			| 'confirmText'
			| 'cancelText'
			| 'required'
			| 'multiline'
			| 'inputType'
		>
	>;

export interface UsePromptDialogReturn {
	/**
	 * Opens the prompt and resolves the value as typed (untrimmed, possibly
	 * empty unless `required`), or `null` when the user cancels or the screen
	 * goes away first. Like `window.prompt`, an empty answer and a cancel are
	 * different results.
	 */
	requestText: (request: PromptRequest) => Promise<string | null>;
	/** Render this anywhere in the calling component; it is null while closed. */
	PromptDialogComponent: React.ReactElement | null;
}

interface PendingPromptModel {
	/** Remounts the dialog per request, so each one starts from its default. */
	key: number;
	request: PromptRequest;
}

/**
 * An awaitable in-app text prompt, replacing `window.prompt` (PD-15134).
 *
 * ```tsx
 * const { requestText, PromptDialogComponent } = usePromptDialog();
 *
 * const reason = await requestText({
 *   title: t('Reject purchase order'),
 *   label: t('Reason (the buyer will see this)'),
 *   required: true,
 *   multiline: true,
 * });
 * if (reason == null) return; // cancelled
 * ```
 *
 * Rendered inside the caller for the same reasons as `useConfirmDialog`.
 */
export function usePromptDialog(): UsePromptDialogReturn {
	const [pending, setPending] = useState<PendingPromptModel | null>(null);
	const resolverRef = useRef<((value: string | null) => void) | null>(null);
	const nextKeyRef = useRef(0);

	const settle = useCallback((value: string | null): void => {
		const resolve = resolverRef.current;
		resolverRef.current = null;
		setPending(null);
		resolve?.(value);
	}, []);

	const requestText = useCallback(
		(request: PromptRequest): Promise<string | null> => {
			// Only one question at a time; a second request cancels the first.
			resolverRef.current?.(null);
			nextKeyRef.current += 1;
			const key = nextKeyRef.current;
			return new Promise<string | null>((resolve) => {
				resolverRef.current = resolve;
				setPending({ key, request });
			});
		},
		[],
	);

	useEffect(
		() => () => {
			resolverRef.current?.(null);
			resolverRef.current = null;
		},
		[],
	);

	const handleSubmit = useCallback(
		(value: string): void => {
			settle(value);
		},
		[settle],
	);
	const handleCancel = useCallback((): void => {
		settle(null);
	}, [settle]);

	const PromptDialogComponent =
		pending != null ? (
			<PromptDialog
				key={pending.key}
				isOpen
				{...pending.request}
				onSubmit={handleSubmit}
				onCancel={handleCancel}
			/>
		) : null;

	return { requestText, PromptDialogComponent };
}
